import React, { useEffect, useState } from 'react';
import { readRegistrationStatus } from '../../services/protectBuild';
import { EYEBROW, NOTE } from './typeScale';
import Pill from './Pill';
/* The same vocabulary and the same cadence the console's credentials card uses,
   so one flip is not reported at two different moments. */
import { awaitingFirstCall, isVerified, REGISTRATION_POLL_MS }
  from '../../data/registrationStatus';

/**
 * Where the registration stands, at the end of Register.
 *
 * <h3>Why this is a step</h3>
 *
 * The console closes its registration here: the row is on file, and the first
 * successful protected call flips it from PENDING_VERIFICATION to VERIFIED with
 * no manual Verify step. Protect used to go straight from "wiring taken" to the
 * next stage, so somebody could finish a registration without being told their
 * agent was not verified yet — and therefore without knowing that the thing
 * which verifies it is making the first call.
 *
 * <h3>The state is read, never written</h3>
 *
 * `registrationStatus` comes off the onboard envelope's own registration row
 * (set at ExternalAgentRegistrationService:370, flipped by the first-call
 * path). Writing the state into the sentence would keep saying
 * PENDING_VERIFICATION after the platform had moved on.
 */
export default function RegisteredAsk({
  envelope, name, path, agentId = null, onAnswer, busy = false,
}) {
  const reg = envelope?.registration;
  const committed = reg?.registrationStatus || null;

  /* The state the platform has NOW, not the one the commit handed back.
     PENDING_VERIFICATION is not a resting state — the first protected call
     flips it, with nothing pressed — so a status read once and shown forever
     goes on saying "the first call verifies it" after the first call has. */
  const [observed, setObserved] = useState(null);
  const status = observed || committed;
  const pending = awaitingFirstCall(status);

  useEffect(() => {
    const id = agentId || reg?.agentId || null;
    if (!id || !awaitingFirstCall(status)) return undefined;
    let live = true;
    const tick = async () => {
      const out = await readRegistrationStatus(id);
      /* A failed read is not a flip. Keep what we had rather than report a
         state nobody came back with. */
      if (live && out.status && out.status !== status) setObserved(out.status);
    };
    const handle = setInterval(tick, REGISTRATION_POLL_MS);
    return () => { live = false; clearInterval(handle); };
  }, [agentId, reg?.agentId, status]);

  /* The KIND of each credential, not the minted row's name. Those names carry
     their own middle dots — "Claude · Cowork · KEV · gateway · 6f606173" — so
     joining them with another one ran them into a single unreadable line where
     the separator and the content were the same character. Which credentials
     exist is the readable fact; the full name is on the agent's own page. */
  const issued = [
    ['Gateway', envelope?.gatewayKey],
    ['SDK', envelope?.sdkKey],
    ['MCP', envelope?.mcpKey],
  ].filter(([, k]) => k).map(([kind]) => kind);

  /* Said out loud. An envelope with no registration is a read that did not
     come back, not an agent in an unknown state, and somebody walking away
     from a blank panel would believe the registration landed. */
  if (!reg) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
        <p style={NOTE}>
          Its registration could not be read back just now. It is on the agent&rsquo;s own
          page in the full platform, with whatever state the platform has for it.
        </p>
        <Continue busy={busy} onAnswer={onAnswer} status={null} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      <p style={NOTE}>
        {pending
          ? 'It is registered and protected. The first protected call it makes verifies it '
            + '— there is nothing to press.'
          : isVerified(status)
            ? 'It is registered, protected and verified.'
            : 'It is registered and protected.'}
      </p>

      <div style={PANEL}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '9px' }}>
          <span style={EYEBROW}>Registration</span>
          <Pill tone={pending ? 'hold' : 'allow'} style={{ marginLeft: 'auto' }}>
            {status || 'Unknown'}
          </Pill>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
          <Fact label="Agent" value={name} />
          {reg.agentId ? <Fact label="Id" value={reg.agentId} mono /> : null}
          <Fact label="Calls arrive"
                value={path === 'MCP' ? 'Through the gateway' : 'Through the SDK, in process'} />
          {issued.length ? <Fact label="Issued" value={issued.join(' · ')} /> : null}
        </div>
      </div>

      <p style={NOTE}>
        Its record is already open — the registration is written to the audit trail, and
        the call that verifies it will be written there too.
      </p>

      <Continue busy={busy} onAnswer={onAnswer} status={status} />
    </div>
  );
}

function Continue({ busy, onAnswer, status }) {
  return (
    <div>
      <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
              onClick={() => onAnswer({ seen: true, registrationStatus: status })}>
        Continue
      </button>
    </div>
  );
}

function Fact({ label, value, mono }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
      <span style={EYEBROW}>{label}</span>
      <span className={mono ? 'p7-mono' : undefined}
            style={{ font: 'var(--p7-text-body)',
                     fontFamily: mono ? undefined : 'var(--p7-font-sans)',
                     fontSize: mono ? '12px' : undefined, color: 'var(--p7-ink)' }}>
        {value}
      </span>
    </span>
  );
}

const PANEL = {
  border: '1px solid var(--p7-line)', borderRadius: 'var(--p7-radius-control)',
  padding: '11px 13px', background: 'var(--p7-surface-sunken)',
};
